import * as z from "zod";

/** The app has no sign-in its connector can offer until its own auth is set up. Clients show their own copy. */
export const CONNECTOR_AUTH_UNAVAILABLE = "CONNECTOR_AUTH_UNAVAILABLE";

/** The app's sign-in needs details from the user first; the error's data lists them. */
export const CONNECTOR_FIELDS_REQUIRED = "CONNECTOR_FIELDS_REQUIRED";

export const ConnectionFieldSchema = z.object({
  name: z.string().min(1).max(64),
  /** The provider's own label, shown when no known `kind` names the field. */
  label: z.string().max(200),
  /** A field clients label themselves; `store_address` takes a shop's address. */
  kind: z.enum(["store_address"]).optional(),
});
export type ConnectionField = z.infer<typeof ConnectionFieldSchema>;
/** What `connections.begin` accepts for the fields; values are validated by the connector. */
export const ConnectionFieldValuesSchema = z
  .record(z.string().min(1).max(64), z.string().max(512))
  .refine((values) => Object.keys(values).length <= 10, "Too many fields");
const ConnectorFieldsRequiredDataSchema = z.object({
  fields: z.array(ConnectionFieldSchema).min(1).max(10),
  invalid: z.array(z.string()).default([]),
});
export type ConnectorFieldsRequiredData = z.infer<typeof ConnectorFieldsRequiredDataSchema>;

/** The fields a `CONNECTOR_FIELDS_REQUIRED` error asks for, and which entered values were refused. */
export function connectorFieldsRequiredData(
  error: unknown,
): ConnectorFieldsRequiredData | undefined {
  if (!error || typeof error !== "object") return undefined;
  const { code, data } = error as { code?: unknown; data?: unknown };
  if (code !== CONNECTOR_FIELDS_REQUIRED) return undefined;
  const parsed = ConnectorFieldsRequiredDataSchema.safeParse(data);
  return parsed.success ? parsed.data : undefined;
}

/** What a `CONNECTOR_AUTH_UNAVAILABLE` error carries: the app, and whether this user can set up its auth. */
export type ConnectorAuthUnavailableData = { app: string; canConfigure: boolean };

/** The error's data when it is a `CONNECTOR_AUTH_UNAVAILABLE` error that names its app. */
export function connectorAuthUnavailableData(
  error: unknown,
): ConnectorAuthUnavailableData | undefined {
  if (!error || typeof error !== "object") return undefined;
  const { code, data } = error as { code?: unknown; data?: unknown };
  if (code !== CONNECTOR_AUTH_UNAVAILABLE || !data || typeof data !== "object") return undefined;
  const { app, canConfigure } = data as { app?: unknown; canConfigure?: unknown };
  if (typeof app !== "string" || !app.trim()) return undefined;
  return { app: app.trim(), canConfigure: canConfigure === true };
}

/** The MCP server's authorization server cannot register clients itself, so the user must
 * enter a client registered with it. Also the health code of a server that hit this. */
export const MCP_OAUTH_CLIENT_REQUIRED = "MCP_OAUTH_CLIENT_REQUIRED";
